import { TOPIC_NODE_UNPLACED_ID, isParkedSubtopicId } from "@okf/ootils";

/**
 * Shape the two aggrMetrics topic-dimension results (level "topic" and
 * level "subtopic") into the SAME group contract the prevalence panel's
 * sub-theme sections render — one section per topic, its sub-topics as the
 * bars — so the existing TaxonomySection + BarChart draw topics with no
 * changes of their own.
 *
 * The keys deliberately mirror groupAnnotationDataBySubTheme's output
 * (subThemeId / subThemeDisplay / tags) because that is what the section
 * reads today; `isTopicGroup` marks these so the panel can route a bar
 * click to the Topics filter instead of the tags filter and skip the
 * tag-only colour-code button.
 *
 *   - topicRows:    [{ value: topicId, display, count, percentage }] — one
 *                    per topic plus the root "Unplaced" row
 *   - subtopicRows: [{ value: subtopicId, display, count, percentage,
 *                      parent: { _id: topicId, display } }] — each topic's
 *                    parked annotations arrive as a pseudo sub-topic
 *                    ("__unplaced:<topicId>", labelled Unplaced); the root
 *                    Unplaced row has no parent
 *
 * Every count and percentage is BE-stamped (share of all annotations in
 * scope); nothing is recomputed here. Topics sort by count desc with the
 * root Unplaced pinned last; within a topic the sub-topics sort by count
 * desc with its Unplaced pseudo sub-topic pinned last.
 */
export const groupTopicDataByTopic = ({ topicRows, subtopicRows }) => {
  if (!Array.isArray(topicRows) || !topicRows.length) return [];

  const groups = {};
  for (const row of topicRows) {
    const id = String(row.value);
    groups[id] = {
      subThemeId: id,
      subThemeDisplay: row.display,
      taxonomyRole: "topic",
      isTopicGroup: true,
      total: row.count || 0,
      percentage: row.percentage || 0,
      tags: [],
    };
  }

  for (const row of subtopicRows || []) {
    const parentId = row.parent?._id ? String(row.parent._id) : TOPIC_NODE_UNPLACED_ID;
    const group = groups[parentId];
    if (!group) continue;
    group.tags.push({
      value: String(row.value),
      display: row.display,
      count: row.count || 0,
      percentage: row.percentage || 0,
      // A breakdown dimension arrives as `children` on each row — passed
      // through as-is (buildAnnoBarPaths turns them into the bar's series).
      ...(row.children ? { children: row.children } : {}),
    });
  }

  const unplacedLast = (a, b) => {
    const aU = isParkedSubtopicId(a.value) || a.value === TOPIC_NODE_UNPLACED_ID;
    const bU = isParkedSubtopicId(b.value) || b.value === TOPIC_NODE_UNPLACED_ID;
    if (aU !== bU) return aU ? 1 : -1;
    return (b.count || 0) - (a.count || 0);
  };

  return Object.values(groups)
    .map((g) => ({ ...g, tags: [...g.tags].sort(unplacedLast) }))
    .sort((a, b) => {
      const aU = a.subThemeId === TOPIC_NODE_UNPLACED_ID;
      const bU = b.subThemeId === TOPIC_NODE_UNPLACED_ID;
      if (aU !== bU) return aU ? 1 : -1;
      return (b.total || 0) - (a.total || 0);
    });
};
